import { useEffect, useRef, useState } from 'react';
import { api } from '../lib/api.js';
import { MicButton } from '../components/MicButton.js';
import { LanguageSwitch } from '../components/LanguageSwitch.js';
import { usePrefs, type Language } from '../lib/prefs.js';
import { useT } from '../lib/i18n.js';
import { useTwinName } from '../lib/twin.js';

type Tab = 'quick' | 'topics' | 'review';
const TABS: { id: Tab; label: string }[] = [
  { id: 'quick', label: 'Quick' }, { id: 'topics', label: 'Topics' }, { id: 'review', label: 'Review' },
];

export function Reflect() {
  const [tab, setTab] = useState<Tab>('quick');
  return (
    <div className="px-5 md:px-10 pb-12 max-w-2xl mx-auto">
      <div className="flex items-center justify-center gap-2 pt-3 pb-6">
        {TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-1.5 rounded-full text-[12px] transition-colors ${ tab === t.id ? 'bg-ink text-void border border-ink' : 'border border-ink/15 text-ink/55 hover:text-ink hover:border-ink/30' }`}>
            {t.label}
          </button>
        ))}
      </div>
      {tab === 'quick' ? <QuickReflect /> : tab === 'topics' ? <TopicReflect /> : <ReviewReflect />}
    </div>
  );
}

// ── Quick: fast this-or-that / scale / multi / one-line prompts ──────────────
type Kind = 'choice' | 'scale' | 'multi' | 'short';
interface Prompt { id: string; kind: Kind; text: string; options?: string[] }

function QuickReflect() {
  const [prefs] = usePrefs();
  const tr = useT();
  const [lang, setLang] = useState<Language>(prefs.language);
  const [prompt, setPrompt] = useState<Prompt | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [text, setText] = useState('');
  const [today, setToday] = useState(0);
  const [streak, setStreak] = useState(0);

  async function loadStats() {
    try { const s = await api<{ answeredToday: number; streak: number }>('/api/reflections/stats'); setToday(s.answeredToday); setStreak(s.streak); } catch { /* ignore */ }
  }
  async function loadNext() {
    setLoading(true); setError(false); setText('');
    try {
      const r = await api<{ prompt: Prompt | null }>(`/api/reflections/next?lang=${lang}`);
      setPrompt(r.prompt);
    } catch { setError(true); }
    finally { setLoading(false); }
  }
  useEffect(() => { void loadNext(); void loadStats(); /* eslint-disable-next-line */ }, [lang]);

  async function answer(value: string) {
    if (!prompt || !value.trim()) return;
    try {
      await api('/api/reflections', { method: 'POST', body: JSON.stringify({ promptId: prompt.id, prompt: prompt.text, answer: value, language: lang }) });
      void loadStats(); void loadNext();
    } catch { setError(true); }
  }

  return (
    <div className="min-h-[58vh] grid place-items-center">
      <div className="w-full text-center space-y-10">
        <div className="flex items-center justify-center gap-3">
          <span className="text-[12px] text-amber/70">
            {tr('reflect.capturedToday', { n: today })}{streak > 0 ? ` · ${tr('reflect.streak')} ${streak}` : ''}
          </span>
          <LanguageSwitch value={lang} onChange={setLang} size="sm" />
        </div>
        {loading ? (
          <div className="text-[12px] text-ink/40">…</div>
        ) : error ? (
          <div className="space-y-3">
            <p className="font-serif italic text-ink/50">{tr('common.unreachable')}</p>
            <button onClick={loadNext} className="px-4 py-2 rounded-full border border-ink/20 text-[11.5px] text-ink/60 hover:text-ink">{tr('common.retry')}</button>
          </div>
        ) : !prompt ? (
          <p className="font-serif italic text-ink/50 text-xl">{tr('reflect.allDone')}</p>
        ) : (
          <div className="space-y-8">
            <p className="font-serif text-3xl text-ink leading-snug">{prompt.text}</p>
            {(prompt.kind === 'choice' || prompt.kind === 'multi') && prompt.options && (
              <div className="flex flex-wrap gap-3 justify-center">
                {prompt.options.map((opt) => (
                  <button key={opt} onClick={() => answer(opt)}
                    className={`rounded-2xl border border-ink/15 bg-white/[0.02] font-serif text-ink hover:border-amber hover:bg-amber/[0.06] transition-colors ${
                      prompt.kind === 'choice' ? 'px-8 py-5 text-xl min-w-[160px]' : 'px-5 py-3 text-lg'
                    }`}>{opt}</button>
                ))}
              </div>
            )}
            {prompt.kind === 'scale' && (
              <div className="flex gap-3 justify-center">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button key={n} onClick={() => answer(String(n))}
                    className="w-14 h-14 rounded-full border border-ink/15 font-serif text-xl text-ink hover:border-amber hover:bg-amber/[0.06] transition-colors">{n}</button>
                ))}
              </div>
            )}
            {prompt.kind === 'short' && (
              <div className="flex flex-col items-center gap-4">
                <div className="w-full max-w-lg flex items-center gap-2">
                  <input value={text} autoFocus onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') answer(text); }}
                    placeholder="…" className="flex-1 px-5 py-3 rounded-2xl border border-ink/15 bg-white/[0.02] font-serif text-xl text-center text-ink focus:border-ink/40 focus:outline-none" />
                  <MicButton size={40} language={lang} onAppend={(t) => setText((s) => (s ? s + ' ' + t : t))} />
                </div>
                <button onClick={() => answer(text)} disabled={!text.trim()}
                  className="px-6 py-2.5 rounded-full bg-ink text-void text-[11.5px] hover:bg-particle disabled:opacity-30">{tr('reflect.capture')}</button>
              </div>
            )}
            <button onClick={loadNext} className="text-[11.5px] text-ink/35 hover:text-ink/60">{tr('reflect.skip')}</button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Topics: the twin interviews Martin on a subject → saved to the vault ──────
interface Topic { id: string; label: string; chapter: string }

function TopicReflect() {
  const [prefs] = usePrefs();
  const tr = useT();
  const [lang, setLang] = useState<Language>(prefs.language);
  const twinName = useTwinName();
  const [topics, setTopics] = useState<Topic[]>([]);
  const [topic, setTopic] = useState<Topic | null>(null);
  const [question, setQuestion] = useState('');
  const [history, setHistory] = useState<Array<{ q: string; a: string }>>([]);
  const [answer, setAnswer] = useState('');
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(0);
  const endRef = useRef<HTMLDivElement>(null);

  useEffect(() => { api<{ topics: Topic[] }>(`/api/reflections/topics?lang=${lang}`).then((r) => setTopics(r.topics)).catch(() => {}); }, [lang]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [history.length, question]);

  async function start(t: Topic) {
    setBusy(true); setTopic(t); setHistory([]); setQuestion(''); setAnswer('');
    try {
      const r = await api<{ topic: Topic; question: string }>('/api/reflections/topic/start', { method: 'POST', body: JSON.stringify({ topicId: t.id, lang }) });
      setQuestion(r.question);
    } catch { setQuestion('…'); }
    finally { setBusy(false); }
  }
  function randomTopic() { if (topics.length) void start(topics[Math.floor(Math.random() * topics.length)]!); }

  async function submit() {
    const a = answer.trim();
    if (!a || !topic || busy) return;
    setBusy(true);
    const askedQ = question;
    try {
      const r = await api<{ memoryId: string; nextQuestion: string }>('/api/reflections/topic/answer', {
        method: 'POST', body: JSON.stringify({ topicId: topic.id, lang, question: askedQ, answer: a, history }),
      });
      setHistory((h) => [...h, { q: askedQ, a }]);
      setQuestion(r.nextQuestion);
      setAnswer('');
      setSaved((s) => s + 1);
    } catch { /* keep the answer so nothing is lost */ }
    finally { setBusy(false); }
  }

  // Topic picker
  if (!topic) {
    return (
      <div className="space-y-6">
        <div className="text-center space-y-2">
          <p className="font-serif text-2xl text-ink">De quoi veux-tu qu’on parle ?</p>
          <p className="font-serif italic text-[14px] text-ink/55">{twinName} te pose des questions ; tes réponses sont enregistrées dans le vault.</p>
          <div className="flex justify-center pt-1"><LanguageSwitch value={lang} onChange={setLang} size="sm" /></div>
        </div>
        <div className="flex flex-wrap gap-2 justify-center">
          {topics.map((t) => (
            <button key={t.id} onClick={() => start(t)}
              className="px-4 py-2 rounded-full border border-ink/15 font-serif text-[15px] text-ink/85 hover:border-amber hover:bg-amber/[0.06] transition-colors">{t.label}</button>
          ))}
        </div>
        <div className="text-center">
          <button onClick={randomTopic} disabled={!topics.length}
            className="px-5 py-2.5 rounded-full bg-amber text-void text-[11.5px] hover:bg-particle disabled:opacity-30">Sujet au hasard →</button>
        </div>
      </div>
    );
  }

  // Conversation
  return (
    <div className="min-h-[60vh] flex flex-col">
      <div className="flex items-center justify-between mb-3 shrink-0">
        <span className="text-[11.5px] text-amber/70">{topic.label} · {saved} enregistré{saved > 1 ? 's' : ''}</span>
        <button onClick={() => setTopic(null)} className="text-[11px] text-ink/40 hover:text-ink">changer de sujet</button>
      </div>

      <div className="flex-1 overflow-y-auto space-y-3 pr-1">
        {history.map((h, i) => (
          <div key={i} className="space-y-1.5">
            <p className="font-serif italic text-[15px] text-ink/55">{h.q}</p>
            <div className="flex justify-end"><div className="max-w-[85%] px-4 py-2 rounded-2xl rounded-br-sm bg-ink text-void font-serif text-[15px] whitespace-pre-wrap">{h.a}</div></div>
          </div>
        ))}
        {/* Current question */}
        <p className="font-serif text-2xl text-ink leading-snug pt-2">{busy && !question ? '…' : question}</p>
        <div ref={endRef} />
      </div>

      {/* Answer composer */}
      <div className="mt-4 flex items-end gap-2 shrink-0">
        <textarea value={answer} onChange={(e) => setAnswer(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void submit(); } }}
          placeholder={tr('reflect.answerPlaceholder')} rows={2} disabled={busy}
          className="flex-1 px-4 py-3 rounded-2xl border border-ink/15 bg-white/[0.02] font-serif text-[16px] text-ink focus:border-ink/40 focus:outline-none resize-none" />
        <LanguageSwitch value={lang} onChange={setLang} size="sm" />
        <MicButton size={40} language={lang} onAppend={(t) => setAnswer((s) => (s ? s + ' ' + t : t))} />
        <button onClick={() => void submit()} disabled={!answer.trim() || busy}
          className="px-5 py-3 rounded-full bg-ink text-void text-[11.5px] hover:bg-particle disabled:opacity-30">{busy ? '…' : 'Envoyer'}</button>
      </div>
      <p className="mt-2 font-mono text-[9px] text-ink/35 text-center">Chaque réponse devient un souvenir dans ton vault.</p>
    </div>
  );
}

// ── Review: revisit / edit / delete past reflections ─────────────────────────
interface ReflItem { id: string; createdAt: number | null; body: string; promptKind: string; topic: string | null }

function ReviewReflect() {
  const tr = useT();
  const [items, setItems] = useState<ReflItem[] | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [draft, setDraft] = useState('');

  async function load() { try { const r = await api<{ reflections: ReflItem[] }>('/api/reflections/list'); setItems(r.reflections); } catch { setItems([]); } }
  useEffect(() => { void load(); }, []);

  async function del(id: string) {
    if (!confirm(tr('reflect.deleteConfirm'))) return;
    try { await api(`/api/memory/${id}`, { method: 'DELETE' }); setItems((x) => x?.filter((i) => i.id !== id) ?? null); } catch { /* ignore */ }
  }
  async function save(id: string) {
    try { await api(`/api/memory/${id}`, { method: 'PATCH', body: JSON.stringify({ body: draft }) }); setItems((x) => x?.map((i) => (i.id === id ? { ...i, body: draft } : i)) ?? null); } catch { /* ignore */ }
    setEditing(null);
  }

  if (!items) return <div className="text-center text-[12px] text-ink/40">…</div>;
  if (items.length === 0) return <p className="text-center font-serif italic text-ink/45 py-10">Aucune réflexion encore. Commence dans Quick ou Topics.</p>;

  const btn = 'px-2.5 py-0.5 rounded-full border text-[10px]';
  return (
    <ul className="space-y-3">
      <li className="text-[11.5px] text-ink/40 text-center pb-1">{items.length} reflections</li>
      {items.map((it) => {
        const [q, ...rest] = it.body.split('\n— ');
        const a = rest.join('\n— ');
        return (
          <li key={it.id} className="border border-ink/10 rounded-xl p-4">
            <div className="flex items-center justify-between gap-3 mb-1.5">
              <span className="text-[11px] text-ink/40">
                {it.promptKind}{it.topic ? ` · ${it.topic}` : ''}{it.createdAt ? ` · ${new Date(it.createdAt).toLocaleDateString()}` : ''}
              </span>
              <div className="flex gap-1.5 flex-shrink-0">
                {editing === it.id ? (
                  <>
                    <button onClick={() => save(it.id)} className={`${btn} border-amber/40 text-amber/80 hover:text-amber`}>save</button>
                    <button onClick={() => setEditing(null)} className={`${btn} border-ink/15 text-ink/45 hover:text-ink`}>cancel</button>
                  </>
                ) : (
                  <>
                    <button onClick={() => { setEditing(it.id); setDraft(it.body); }} className={`${btn} border-ink/15 text-ink/45 hover:text-ink`}>edit</button>
                    <button onClick={() => del(it.id)} className={`${btn} border-red-300/25 text-red-300/70 hover:text-red-300`}>✕</button>
                  </>
                )}
              </div>
            </div>
            {editing === it.id ? (
              <textarea value={draft} onChange={(e) => setDraft(e.target.value)} rows={3}
                className="w-full px-3 py-2 rounded-lg border border-ink/15 bg-white/[0.02] font-serif text-[14px] text-ink focus:border-ink/40 focus:outline-none resize-none" />
            ) : (
              <>
                {q && a && <p className="font-serif italic text-[13.5px] text-ink/55 leading-snug">{q}</p>}
                <p className="font-serif text-[15px] text-ink/85 leading-relaxed whitespace-pre-wrap">{a || it.body}</p>
              </>
            )}
          </li>
        );
      })}
    </ul>
  );
}
